/**
 * Studio → Bundles (index) — #152.
 *
 * List view at /studio/bundles. Shows all bundles visible to the
 * requester: platform-authored industry kits (read-only) + this tenant's
 * own drafts and publishes. Industry-tag filter pills along the top.
 *
 * Each row links into the detail page (bundles.$id.tsx) where the
 * tenant can inspect the manifest and trigger Install (for published)
 * or Publish (for own drafts).
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Package, Search, AlertTriangle, Plus, Factory, ShoppingBag, Heart,
  Zap, Briefcase, Boxes, Eye, Lock,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBundles, type BundleRow, type IndustryTag } from '@/lib/use-studio-bundles';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/studio/bundles/')({
  component: StudioBundlesIndexPage,
});

// ─── Industry presentation ─────────────────────────────────────────────

const INDUSTRY_META: Record<
  string,
  { label: string; cls: string; icon: typeof Factory }
> = {
  manufacturing: {
    label: 'Manufacturing',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: Factory,
  },
  retail: {
    label: 'Retail',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: ShoppingBag,
  },
  healthcare: {
    label: 'Healthcare',
    cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
    icon: Heart,
  },
  energy: {
    label: 'Energy',
    cls: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/30',
    icon: Zap,
  },
  finance: {
    label: 'Finance',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    icon: Briefcase,
  },
  general: {
    label: 'General',
    cls: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
    icon: Boxes,
  },
};

const STATUS_META: Record<
  string,
  { label: string; cls: string }
> = {
  draft: {
    label: 'Draft',
    cls: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
  },
  published: {
    label: 'Published',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  archived: {
    label: 'Archived',
    cls: 'bg-slate-400/10 text-slate-500 dark:text-slate-400 border-slate-400/30',
  },
};

const INDUSTRY_FILTER_OPTIONS: Array<{ value: IndustryTag | 'all'; label: string }> = [
  { value: 'all', label: 'All industries' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'energy', label: 'Energy' },
  { value: 'finance', label: 'Finance' },
  { value: 'general', label: 'General' },
];

function StudioBundlesIndexPage() {
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState<IndustryTag | 'all'>('all');
  const navigate = useNavigate();

  const { data, isLoading, isError } = useBundles({
    industry: industryFilter === 'all' ? undefined : industryFilter,
  });

  const filtered = useMemo<BundleRow[]>(() => {
    const all = data?.items ?? [];
    if (!search.trim()) return all;
    const needle = search.toLowerCase();
    return all.filter(
      (b) =>
        b.name.toLowerCase().includes(needle) ||
        b.slug.toLowerCase().includes(needle) ||
        b.description.toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        <div className="p-4 border-b bg-card/50 backdrop-blur">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Package className="h-5 w-5 text-primary" />
              <h2 className="text-base font-semibold">Bundles &amp; Industry Kits</h2>
              <span className="text-xs text-muted-foreground">
                Curated packages of tools, skills, prompts, agents, MCP servers,
                and knowledge — install with one click to bootstrap a workspace.
              </span>
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: '/studio/bundles/new' })}
            >
              <Plus className="h-3.5 w-3.5" /> New Bundle
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, slug, or description"
                className="pl-7 h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {INDUSTRY_FILTER_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={industryFilter === opt.value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setIndustryFilter(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {isError && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                Failed to load bundles. Refresh the page or check API connectivity.
              </CardContent>
            </Card>
          )}
          {!isError && !isLoading && filtered.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
                <p className="font-medium text-foreground">No bundles match the current filter.</p>
                <p className="text-xs mt-1">
                  Clear the industry filter, or create a new bundle to package up
                  the catalog entities you use most.
                </p>
              </CardContent>
            </Card>
          )}
          {!isError && filtered.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((b) => {
                      const meta = INDUSTRY_META[b.industryTag] ?? INDUSTRY_META.general;
                      const Icon = meta.icon;
                      const statusMeta = STATUS_META[b.status] ?? STATUS_META.draft;
                      return (
                        <TableRow key={b.id}>
                          <TableCell>
                            <Link
                              to="/studio/bundles/$id"
                              params={{ id: b.id }}
                              className="block hover:underline"
                            >
                              <div className="font-medium text-sm">{b.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {b.slug} · {b.description.slice(0, 80)}
                                {b.description.length > 80 ? '…' : ''}
                              </div>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={cn('gap-1', meta.cls)}>
                              <Icon className="h-3 w-3" />
                              {meta.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {b.itemCount}
                          </TableCell>
                          <TableCell className="text-xs font-mono">
                            {b.version}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusMeta.cls}>
                              {statusMeta.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {b.author === 'platform' ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className="gap-1 bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
                                  >
                                    <Lock className="h-3 w-3" /> Platform
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Authored by the platform vendor. Read-only;
                                  install to clone into your workspace.
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Badge variant="outline">Tenant</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Open bundle detail"
                              onClick={() =>
                                navigate({ to: '/studio/bundles/$id', params: { id: b.id } })
                              }
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
